import { min, max } from 'd3-array';
import { buildChartTimeline } from '../utils/buildChartTimeline';
import { normalizeChartCandles } from '../utils/normalizeChartCandles';

/**
 * Normalizes a raw `getChart` response into bucketed candles + session windows, without
 * building any chart-rendering state. Shared by `evaluateChartResponse` (kept for callers that
 * still need local chart state during migration — see PRD Functional Requirement #7) and
 * `RefactoredChartContainer`'s store-seeding path, which needs the normalized candles to hand
 * to `chartCandleStore.seedFromGetChart` but does not need `buildChartState`'s timeline/bounds
 * computation for that step (the store-driven render reaction builds that separately).
 */
export function normalizeGetChartResponse({ msg, timeUnit, candleDuration, numberOfCandles }) {
  const normalizedSessionWindows = normalizeSessionWindows(msg?.sessionWindows ?? []);
  const normalizedCandles = normalizeChartCandles({
    candles: msg?.candles ?? [],
    timeUnit,
    candleDuration,
    sessionWindows: normalizedSessionWindows,
  }).slice(-numberOfCandles);

  return {
    normalizedCandles,
    normalizedSessionWindows,
  };
}

/**
 * Builds local chart-rendering state (oldCandles/currentCandle/timeline/bounds) from a
 * `getChart` response. Retry-on-partial-data has been removed per the PRD: a previous version
 * retried when `candles.length < numberOfCandles`, but after the retry budget was exhausted the
 * code returned with no chart state, blanking a chart that already had real candles to show
 * (see specs/candle-streaming.md). The store-driven render path (`ChartCandleStore` +
 * `RefactoredChartContainer`'s history reaction) is what now fills in partial/sparse data over
 * time; this function's only remaining job is to turn whatever candles a single response
 * contains into renderable state, never to decide whether to ask for more.
 */
export function evaluateChartResponse({
  msg,
  timeUnit,
  candleDuration,
  numberOfCandles,
  millisBetweenCandles,
  size,
  margin,
}) {
  const { normalizedCandles, normalizedSessionWindows } = normalizeGetChartResponse({
    msg,
    timeUnit,
    candleDuration,
    numberOfCandles,
  });

  const chartState =
    normalizedCandles.length === 0
      ? null
      : buildChartState({
          candles: normalizedCandles,
          sessionWindows: normalizedSessionWindows,
          millisBetweenCandles,
          size,
          margin,
        });

  return { chartState };
}

export function buildChartState({
  candles,
  sessionWindows,
  millisBetweenCandles,
  size,
  margin,
}) {
  const timeline = buildChartTimeline({
    candles,
    millisBetweenCandles,
  });
  const timelineCandles = [...timeline.candles];
  const currentCandle = timelineCandles.pop() ?? null;

  return {
    oldCandles: timelineCandles,
    currentCandle,
    realNumberOfCandles: timeline.totalSlots,
    priceMinimum: min(timeline.candles, (c) => c.low),
    priceMaximum: max(timeline.candles, (c) => c.high),
    timeStampArray: timeline.timeStampArray,
    caliber: Math.max(size / timeline.totalSlots - margin, 1),
    gaps: timeline.gaps,
    sessionWindows: normalizeSessionWindows(sessionWindows),
  };
}

function normalizeSessionWindows(sessionWindows) {
  if (!Array.isArray(sessionWindows)) {
    return [];
  }

  return sessionWindows
    .map((sessionWindow) => ({
      session: sessionWindow?.session ?? null,
      startTimeStamp: Number(sessionWindow?.startTimeStamp),
      endTimeStamp: Number(sessionWindow?.endTimeStamp),
      sessionStartTimeStamp: Number(
        sessionWindow?.sessionStartTimeStamp ?? sessionWindow?.startTimeStamp
      ),
      sessionEndTimeStamp: Number(
        sessionWindow?.sessionEndTimeStamp ?? sessionWindow?.endTimeStamp
      ),
    }))
    .filter(
      (sessionWindow) =>
        sessionWindow.session &&
        Number.isFinite(sessionWindow.startTimeStamp) &&
        Number.isFinite(sessionWindow.endTimeStamp) &&
        Number.isFinite(sessionWindow.sessionStartTimeStamp) &&
        Number.isFinite(sessionWindow.sessionEndTimeStamp)
    )
    .sort(
      (left, right) =>
        left.sessionStartTimeStamp - right.sessionStartTimeStamp
    );
}
